import { useCallback, useEffect, useRef, useState } from 'react';

import { parseScript, countTextSegments } from '@/lib/ground/script';
import { useGround } from './useGround';

export type VoiceStatus = 'idle' | 'playing' | 'paused' | 'finished';
export type VoiceEngine = 'elevenlabs' | 'browser' | 'none';

export interface UseGroundVoiceResult {
  status: VoiceStatus;
  /** Index of the text segment currently being spoken (-1 when idle). */
  segmentIndex: number;
  totalSegments: number;
  engine: VoiceEngine;
  /** True when an ElevenLabs key + voice ID are configured. */
  apiConfigured: boolean;
  /** True when the browser SpeechSynthesis API is available. */
  browserAvailable: boolean;
  error: string | null;
  play: (script: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

const ELEVENLABS_BASE = 'https://api.elevenlabs.io/v1/text-to-speech';
const PAUSE_MS = 2600;

function hasSpeechSynthesis(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Narrate a somatic script with ElevenLabs (preferred) or the browser's built-in
 * SpeechSynthesis as a fallback. `{pause}` tokens become real silence.
 */
export function useGroundVoice(): UseGroundVoiceResult {
  const { settings } = useGround();

  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [segmentIndex, setSegmentIndex] = useState(-1);
  const [totalSegments, setTotalSegments] = useState(0);
  const [engine, setEngine] = useState<VoiceEngine>('none');
  const [error, setError] = useState<string | null>(null);

  const runIdRef = useRef(0);
  const cancelledRef = useRef(false);
  const pausedRef = useRef(false);
  const resumeWaitersRef = useRef<Array<() => void>>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const apiFailedRef = useRef(false);

  const apiConfigured = Boolean(settings.elevenLabsApiKey && settings.elevenLabsVoiceId);
  const browserAvailable = hasSpeechSynthesis();

  const releaseResumeWaiters = useCallback(() => {
    const waiters = resumeWaitersRef.current;
    resumeWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, []);

  const teardown = useCallback(() => {
    cancelledRef.current = true;
    pausedRef.current = false;
    releaseResumeWaiters();

    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // Ignore playback teardown errors.
      }
      audioRef.current = null;
    }

    if (hasSpeechSynthesis()) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        // Ignore speech teardown errors.
      }
    }
    utteranceRef.current = null;
  }, [releaseResumeWaiters]);

  // Clean up on unmount.
  useEffect(() => () => teardown(), [teardown]);

  const waitWhilePaused = useCallback(async (myRun: number) => {
    while (pausedRef.current && runIdRef.current === myRun) {
      await new Promise<void>((resolve) => {
        resumeWaitersRef.current.push(resolve);
      });
    }
  }, []);

  const sleep = useCallback(
    (ms: number, myRun: number) =>
      new Promise<void>((resolve) => {
        const start = Date.now();
        const tick = () => {
          if (runIdRef.current !== myRun) return resolve();
          if (pausedRef.current) {
            // Freeze the pause timer while paused.
            setTimeout(tick, 200);
            return;
          }
          if (Date.now() - start >= ms) return resolve();
          setTimeout(tick, Math.min(200, ms - (Date.now() - start)));
        };
        tick();
      }),
    [],
  );

  const speakElevenLabs = useCallback(
    async (text: string, myRun: number) => {
      const response = await fetch(
        `${ELEVENLABS_BASE}/${encodeURIComponent(settings.elevenLabsVoiceId)}`,
        {
          method: 'POST',
          headers: {
            'xi-api-key': settings.elevenLabsApiKey,
            'Content-Type': 'application/json',
            Accept: 'audio/mpeg',
          },
          body: JSON.stringify({
            text,
            model_id: 'eleven_turbo_v2_5',
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.75,
              style: 0.2,
              use_speaker_boost: true,
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`ElevenLabs request failed (${response.status})`);
      }

      const blob = await response.blob();
      if (runIdRef.current !== myRun) return;

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;

      await new Promise<void>((resolve, reject) => {
        audio.onended = () => {
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Audio playback failed'));
        };
        audio.play().catch(reject);
      });

      audioRef.current = null;
    },
    [settings.elevenLabsApiKey, settings.elevenLabsVoiceId],
  );

  const speakBrowser = useCallback((text: string) => {
    return new Promise<void>((resolve) => {
      if (!hasSpeechSynthesis()) {
        resolve();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.pitch = 1;
      utterance.volume = 1;

      const voices = window.speechSynthesis.getVoices();
      const preferred =
        voices.find((voice) => /en[-_]/i.test(voice.lang) && /samantha|female|google/i.test(voice.name)) ??
        voices.find((voice) => /en/i.test(voice.lang));
      if (preferred) utterance.voice = preferred;

      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }, []);

  const stop = useCallback(() => {
    runIdRef.current += 1;
    teardown();
    setStatus('idle');
    setSegmentIndex(-1);
  }, [teardown]);

  const play = useCallback(
    (script: string) => {
      // Cancel any previous run first.
      runIdRef.current += 1;
      teardown();

      const myRun = runIdRef.current;
      cancelledRef.current = false;
      pausedRef.current = false;
      apiFailedRef.current = false;
      setError(null);
      setSegmentIndex(-1);

      const steps = parseScript(script);
      setTotalSegments(countTextSegments(script));

      if (apiConfigured) {
        setEngine('elevenlabs');
      } else if (browserAvailable) {
        setEngine('browser');
      } else {
        setEngine('none');
        setStatus('idle');
        setError('No voice is available. Add an ElevenLabs key in Settings, or use a browser with speech support.');
        return;
      }

      setStatus('playing');

      void (async () => {
        for (const step of steps) {
          if (runIdRef.current !== myRun || cancelledRef.current) return;

          await waitWhilePaused(myRun);
          if (runIdRef.current !== myRun || cancelledRef.current) return;

          if (step.type === 'pause') {
            await sleep(PAUSE_MS, myRun);
            continue;
          }

          setSegmentIndex(step.index);

          let spoken = false;
          if (apiConfigured && !apiFailedRef.current) {
            try {
              await speakElevenLabs(step.text, myRun);
              spoken = true;
            } catch (err) {
              console.warn('Ground: ElevenLabs playback failed, falling back to browser voice.', err);
              apiFailedRef.current = true;
              setEngine(browserAvailable ? 'browser' : 'none');
            }
          }

          if (!spoken && browserAvailable) {
            await speakBrowser(step.text);
          }

          if (runIdRef.current !== myRun || cancelledRef.current) return;
        }

        setStatus('finished');
        setSegmentIndex(-1);
      })();
    },
    [
      apiConfigured,
      browserAvailable,
      sleep,
      speakBrowser,
      speakElevenLabs,
      teardown,
      waitWhilePaused,
    ],
  );

  const pause = useCallback(() => {
    if (!pausedRef.current) {
      pausedRef.current = true;
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {
          // Ignore.
        }
      }
      if (hasSpeechSynthesis() && window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
      }
      setStatus('paused');
    }
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    if (audioRef.current) {
      audioRef.current.play().catch(() => undefined);
    }
    if (hasSpeechSynthesis()) {
      window.speechSynthesis.resume();
    }
    releaseResumeWaiters();
    setStatus('playing');
  }, [releaseResumeWaiters]);

  return {
    status,
    segmentIndex,
    totalSegments,
    engine,
    apiConfigured,
    browserAvailable,
    error,
    play,
    pause,
    resume,
    stop,
  };
}
